import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Bed, Users, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AccBookingInfo {
  id: string;
  guestName: string | null;
  guestEmail: string;
  guests: number;
  checkIn: string;
  checkOut: string;
  status: string;
  alreadyCancelled: boolean;
  accommodationTitle: string;
  producerName: string;
}

type PageState = 'loading' | 'found' | 'cancelled' | 'already_cancelled' | 'error' | 'invalid';

export default function AccommodationBookingCancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [booking, setBooking] = useState<AccBookingInfo | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }
    lookupBooking();
  }, [token]);

  const lookupBooking = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-accommodation-booking?token=${token}`,
        { headers: { 'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
      );
      const result = await response.json();

      if (!response.ok || !result.ok) {
        setPageState('error');
        return;
      }

      setBooking(result.booking);
      setPageState(result.booking.alreadyCancelled ? 'already_cancelled' : 'found');
    } catch {
      setPageState('error');
    }
  };

  const handleCancel = async () => {
    if (!token) return;
    setCancelling(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-accommodation-booking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ token }),
        }
      );
      const result = await response.json();

      if (result.ok) {
        setPageState('cancelled');
        toast.success('Bokningen har avbokats');
      } else {
        toast.error(result.error || 'Kunde inte avboka');
      }
    } catch {
      toast.error('Något gick fel');
    } finally {
      setCancelling(false);
      setShowConfirmDialog(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (pageState === 'invalid') {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
        <h1 className="text-2xl font-bold mb-2">Ogiltig länk</h1>
        <p className="text-muted-foreground">Avbokningslänken saknar giltig token.</p>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
        <h1 className="text-2xl font-bold mb-2">Bokningen hittades inte</h1>
        <p className="text-muted-foreground">Kontrollera att du har rätt länk.</p>
      </div>
    );
  }

  if (pageState === 'cancelled') {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <CheckCircle className="h-12 w-12 mx-auto text-primary mb-4" />
        <h1 className="text-2xl font-bold mb-2">Bokningen har avbokats</h1>
        <p className="text-muted-foreground mb-6">
          {booking?.accommodationTitle} har avbokats. Datumen är nu tillgängliga igen.
        </p>
        <Button asChild variant="outline">
          <Link to="/">Till startsidan</Link>
        </Button>
      </div>
    );
  }

  if (pageState === 'already_cancelled') {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Redan avbokad</h1>
        <p className="text-muted-foreground">Den här bokningen är redan avbokad.</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <Card>
        <CardHeader>
          <CardTitle>Avboka boende</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {booking && (
            <>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Bed className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{booking.accommodationTitle}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{booking.checkIn} → {booking.checkOut}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{booking.guests} gäst{booking.guests > 1 ? 'er' : ''}</span>
                </div>
                <p className="text-muted-foreground">Värd: {booking.producerName}</p>
              </div>
              <Button variant="destructive" className="w-full" onClick={() => setShowConfirmDialog(true)}>
                Avboka
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bekräfta avbokning</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill avboka {booking?.accommodationTitle}? Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Behåll bokning</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} disabled={cancelling} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {cancelling ? 'Avbokar...' : 'Ja, avboka'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
